
import { useTranslation } from 'react-i18next';

import { Fragment, useEffect, useId, useState } from "react";
import { userAPI } from "../UserAPI";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface Properties {
  readonly?: boolean;
  type: string;
  kind?: "asset" | "product";
  properties: any;
  setProperties: any;
}

// Universal dimensional fields surfaced for products regardless of category.
// Names double as storage keys in properties.* (same convention as DIN 276 fields).
const PRODUCT_DIMENSION_FIELDS = [
  { name: "Länge", type: "number", unit: "mm", values: [] as string[] },
  { name: "Breite", type: "number", unit: "mm", values: [] as string[] },
  { name: "Tiefe", type: "number", unit: "mm", values: [] as string[] },
];

export default function DIN276AssetProperties(properties: Properties) {
  const { t } = useTranslation();
  const [type, setType] = useState() as any;
  const isProduct = properties.kind === "product";
  const fieldPrefix = useId();


  useEffect(() => {
    const call = async () => {
      try {
        const metadata = await userAPI.getDIN276Metadata();

        if (!Array.isArray(metadata)) return;

        if (properties.type) {
          // schemaCategory may be a code (e.g. "411.10", set by the panel) or the
          // label (e.g. "Wasseraufbereitungsanlage", set by the crawler import).
          // Try both so foreign products resolve too.
          const needle = String(properties.type).trim();
          setType(
            metadata.find((m: any) => m.code === needle) ||
              metadata.find(
                (m: any) =>
                  (m.name || "").toLowerCase() === needle.toLowerCase(),
              ),
          );
        }
      } catch (error) {
        // Silently fail if metadata cannot be loaded
      }
    };

    call();
  }, [properties.type]);

  const mapQualityState = (id: string) => {
    switch (id) {
      case 'notAcceptable': return t('DIN276AssetProperties.notAcceptable');
      case 'bad': return t('DIN276AssetProperties.bad');
      case 'acceptable': return t('DIN276AssetProperties.acceptable');
      case 'good': return t('DIN276AssetProperties.good');
      case 'veryGood': return t('DIN276AssetProperties.veryGood');
      default: return t('Global.notEntered');
    }
  }

  const qualityStateOptions = [{
    key: 'notAcceptable',
    text: t('DIN276AssetProperties.notAcceptable'),
  }, {
    key: 'bad',
    text: t('DIN276AssetProperties.bad'),
  },
  {
    key: 'acceptable',
    text: t('DIN276AssetProperties.acceptable'),
  }, {
    key: 'good',
    text: t('DIN276AssetProperties.good'),
  }, {
    key: 'veryGood',
    text: t('DIN276AssetProperties.veryGood'),
  }];

  return <>{type && type.fields && properties.properties
    ?
    <div className="min-w-0">
      {properties.readonly
        ?
        /* Label/Wert als Beschreibungsliste — auf schmalen Viewports einspaltig,
           statt der früheren festen 200px-Labelspalte. */
        <dl className="grid grid-cols-1 gap-x-6 gap-y-1 text-sm sm:grid-cols-[max-content_1fr]">
          {isProduct ? null : (
            <>
              <dt className="min-w-0 break-words font-bold">Quality state:</dt>
              <dd className="min-w-0 break-words">
                {properties && properties.properties['qualityState'] ? mapQualityState(properties.properties['qualityState']) : '-'}
              </dd>
            </>
          )}
          {(isProduct ? [...PRODUCT_DIMENSION_FIELDS, ...type.fields] : type.fields).map((field: any) => (
            <Fragment key={field.name}>
              <dt className="min-w-0 break-words font-bold max-sm:mt-2">{field.name}:</dt>
              <dd className="min-w-0 break-words">
                {properties.properties && properties.properties[field.name] ? properties.properties[field.name] : '-'}
              </dd>
            </Fragment>
          ))}
        </dl>
        :
        <div className="grid min-w-0 gap-4 sm:grid-cols-2">
          {isProduct ? null : (
            <div className="flex min-w-0 flex-col gap-1.5">
              <Label htmlFor={`${fieldPrefix}-qualityState`}>Quality state</Label>
              <Select
                value={properties.properties['qualityState'] ?? undefined}
                onValueChange={(value: string) => {
                  const newProperties = { ...properties.properties } as any;

                  newProperties['qualityState'] = value;

                  properties.setProperties(newProperties)
                }}
              >
                <SelectTrigger id={`${fieldPrefix}-qualityState`} className="max-w-full sm:max-w-[300px]">
                  <SelectValue placeholder="Please select ..." />
                </SelectTrigger>
                <SelectContent className="max-w-[calc(100vw-2rem)]">
                  {qualityStateOptions.map((option) => (
                    <SelectItem key={option.key} value={option.key} className="whitespace-normal">
                      {option.text}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {(isProduct ? [...PRODUCT_DIMENSION_FIELDS, ...type.fields] : type.fields).map((field: any) => {
            const fieldId = `${fieldPrefix}-${field.name}`;

            switch (field.type) {
              case 'enum': return (
                <div key={field.name} className="flex min-w-0 flex-col gap-1.5">
                  <Label htmlFor={fieldId}>{field.name}</Label>
                  <Select
                    value={properties.properties[field.name] ?? undefined}
                    onValueChange={(value: string) => {
                      const newProperties = { ...properties.properties } as any;

                      newProperties[field.name] = value;

                      properties.setProperties(newProperties)
                    }}
                  >
                    <SelectTrigger id={fieldId} className="max-w-full sm:max-w-[300px]">
                      <SelectValue placeholder="Bitte auswählen ..." />
                    </SelectTrigger>
                    <SelectContent className="max-w-[calc(100vw-2rem)]">
                      {field.values.map((x: any) => (
                        <SelectItem key={x} value={x} className="whitespace-normal">
                          {x}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              );
              case 'number': return (
                <div key={field.name} className="flex min-w-0 flex-col gap-1.5">
                  <Label htmlFor={fieldId}>{field.name}</Label>
                  <Input
                    id={fieldId}
                    type="number"
                    value={properties.properties[field.name] ?? ''}
                    onChange={(e) => {
                      const newProperties = { ...properties.properties } as any;

                      newProperties[field.name] = e.target.value;

                      properties.setProperties(newProperties)
                    }}
                  />
                </div>
              );
              /* Bestand: Boolesche Felder werden als Zahlenfeld bearbeitet —
                 unverändert übernommen. */
              case 'boolean': return (
                <div key={field.name} className="flex min-w-0 flex-col gap-1.5">
                  <Label htmlFor={fieldId}>{field.name}</Label>
                  <Input
                    id={fieldId}
                    type="number"
                    value={properties.properties[field.name] ?? ''}
                    onChange={(e) => {
                      const newProperties = { ...properties.properties } as any;

                      newProperties[field.name] = e.target.value;

                      properties.setProperties(newProperties)
                    }}
                  />
                </div>
              );
              case 'string':
              default: return (
                <div key={field.name} className="flex min-w-0 flex-col gap-1.5">
                  <Label htmlFor={fieldId}>{field.name}</Label>
                  <Input
                    id={fieldId}
                    value={properties.properties[field.name] ?? ''}
                    onChange={(e) => {
                      const newProperties = { ...properties.properties } as any;

                      newProperties[field.name] = e.target.value;

                      properties.setProperties(newProperties)
                    }}
                  />
                </div>
              );
            }
          })}
        </div>}
    </div>
    :
    <div></div>
  }
  </>;
}
